import { Transition } from "@headlessui/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  type ReactNode,
} from "react";

const FLOATING_ALERT_AUTO_DISMISS_MS = 2000;

type FloatingAlertVariant = "success" | "info";

export interface FloatingAlertOptions {
  content: ReactNode;
  variant?: FloatingAlertVariant;
}

export interface FloatingAlertManagerHandle {
  enqueueAlert: (options: FloatingAlertOptions) => void;
}

export const FloatingAlertManager = forwardRef<FloatingAlertManagerHandle>(
  (_, ref) => {
    const [activeAlert, setActiveAlert] = useState<FloatingAlertOptions | null>(
      null
    );

    const [isOpen, setIsOpen] = useState(false);

    useImperativeHandle(ref, () => ({
      enqueueAlert: ({ content, variant = "success" }) => {
        setActiveAlert({ content, variant });
        setIsOpen(true);
      },
    }));

    // Automatically clear the active alert after a timeout
    useEffect(() => {
      if (!isOpen) {
        return undefined;
      }

      const timeoutId = window.setTimeout(() => {
        setIsOpen(false);
      }, FLOATING_ALERT_AUTO_DISMISS_MS);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }, [isOpen, activeAlert]);

    return (
      <FloatingAlertBanner
        isOpen={isOpen}
        variant={activeAlert?.variant ?? "success"}
      >
        {activeAlert?.content ?? null}
      </FloatingAlertBanner>
    );
  }
);

FloatingAlertManager.displayName = "FloatingAlertManager";

interface FloatingAlertBannerProps {
  isOpen: boolean;
  children: ReactNode;
  variant?: "success" | "info";
}

const FloatingAlertBanner = ({
  isOpen,
  children,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  variant = "success",
}: FloatingAlertBannerProps) => {
  return (
    <Transition
      as="div"
      show={isOpen}
      enter="transition ease-out duration-200"
      enterFrom="scale-y-0 opacity-0"
      enterTo="scale-y-100 opacity-100"
      leave="transition ease-in duration-150"
      leaveFrom="scale-y-100 opacity-100"
      leaveTo="scale-y-0 opacity-0"
      className="absolute top-0 left-0 right-0 z-10 flex h-10 w-full origin-top items-center justify-center px-6"
      style={{
        background: "rgba(39, 169, 37, 0.85)",
        boxShadow:
          "0 0 2px 0 rgba(40, 41, 61, 0.04), 0 4px 8px 0 rgba(96, 97, 112, 0.16)",
        backdropFilter: "blur(2.7px)",
      }}
    >
      <span className="truncate text-sm font-normal leading-[140%] text-white">
        {children}
      </span>
    </Transition>
  );
};
